/**
 * Collection filters & sorting.
 *
 * On filter / sort change the form fetches the collection URL with
 * `?sections=<section-id>` so Shopify returns just the rendered section
 * HTML, then swaps the live section's contents in-place. The URL is
 * updated via history.pushState so the browser back button still works
 * but the page never fully reloads — scroll position, focus, drawer
 * state and any open modals stay put.
 *
 * Mobile: full-screen drawer from left with Apply / Clear buttons.
 * Desktop: inline sidebar that auto-applies on every change.
 */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-filter-form]');
    this.aside = this.querySelector('[data-filters]');
    this.overlay = this.querySelector('[data-filter-overlay]');
    this.label = this.querySelector('[data-filters-label]');
    this.section = this.closest('.shopify-section');

    this.desktopVisible = true;
    this.mdQuery = window.matchMedia('(min-width: 768px)');

    this.querySelectorAll('[data-toggle-filters]').forEach((btn) =>
      btn.addEventListener('click', () => this.toggle())
    );
    this.querySelectorAll('[data-close-filters]').forEach((btn) =>
      btn.addEventListener('click', () => this.closeDrawer())
    );
    this.overlay?.addEventListener('click', () => this.closeDrawer());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawerOpen) this.closeDrawer();
    });

    // Desktop: auto-apply on change. Mobile: user clicks Apply.
    this.form?.addEventListener('change', (e) => {
      if (!this.mdQuery.matches) return;
      if (e.target.type === 'number') {
        clearTimeout(this._debounce);
        this._debounce = setTimeout(() => this.#apply(), 500);
      } else {
        this.#apply();
      }
    });

    // Mobile Apply button
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#apply().then(() => this.closeDrawer());
    });

    // Filter pills, "Clear all" and pagination links: capture clicks and
    // route the URL through the AJAX path so they don't reload either.
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a.pp-collection-pill, a.pp-collection-clear, .pp-collection-page, .pp-collection-loadmore a');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('http')) return;
      e.preventDefault();
      this.#applyUrl(href);
    });

    this.querySelectorAll('[data-range-slider]').forEach((slider) =>
      this.#initRangeSlider(slider)
    );

    this.#updateDesktopLabel();
  }

  get drawerOpen() {
    return this.aside?.classList.contains('is-open');
  }

  toggle() {
    if (this.mdQuery.matches) {
      this.desktopVisible = !this.desktopVisible;
      this.aside.classList.toggle('is-desktop-hidden');
      this.#updateDesktopLabel();
    } else {
      this.drawerOpen ? this.closeDrawer() : this.openDrawer();
    }
  }

  openDrawer() {
    this.aside?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.aside?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  #updateDesktopLabel() {
    if (this.label) {
      this.label.textContent = this.desktopVisible ? 'Hide Filters' : 'Show Filters';
    }
  }

  #setLoading(on) {
    const grid = this.querySelector('[data-product-grid]');
    grid?.classList.toggle('is-loading', !!on);
    this.form?.querySelectorAll('input, select, button').forEach((el) => {
      if (on) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
    });
  }

  /**
   * Apply current form state via AJAX.
   */
  async #apply() {
    if (!this.form) return;
    const data = new FormData(this.form);
    const params = new URLSearchParams();
    for (const [k, v] of data.entries()) {
      if (v === '' || v === null) continue;
      params.append(k, v);
    }
    const url = `${this.form.getAttribute('action') || location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    return this.#applyUrl(url);
  }

  async #applyUrl(url) {
    if (this.#abort) this.#abort.abort();
    this.#abort = new AbortController();
    const sectionId = this.section?.id?.replace(/^shopify-section-/, '');
    if (!sectionId) {
      // Fallback: full reload if we can't figure out the section id.
      window.location.href = url;
      return;
    }

    const sep = url.includes('?') ? '&' : '?';
    const fetchUrl = `${url}${sep}sections=${encodeURIComponent(sectionId)}`;

    this.#setLoading(true);
    try {
      const r = await fetch(fetchUrl, { signal: this.#abort.signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const html = data[sectionId];
      if (!html) throw new Error(`section ${sectionId} not in response`);

      // Pull the inner contents of the new section and swap them in.
      // We replace the section's children (not the section itself) so the
      // wrapping <section id="shopify-section-..."> stays the same node
      // and any external observers don't lose their reference.
      const tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      const newSection = tpl.content.firstElementChild;
      const newCollectionFilters = newSection?.querySelector('collection-filters');
      const oldChildren = Array.from(this.section.children);
      if (!newCollectionFilters) throw new Error('no collection-filters in response');
      // Replace the section's direct children with the fresh ones.
      // (`this` is about to be detached; we do not run any code after the
      // replace.)
      while (this.section.firstChild) this.section.removeChild(this.section.firstChild);
      while (newSection.firstChild) this.section.appendChild(newSection.firstChild);

      // Update browser URL (preserve scroll, no reload).
      history.pushState({}, '', url);

      document.dispatchEvent(new CustomEvent('collection:updated', { detail: { url } }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Filter apply failed; falling back to full reload:', err);
      window.location.href = url;
    }
  }

  #initRangeSlider(container) {
    const minThumb = container.querySelector('[data-range-min]');
    const maxThumb = container.querySelector('[data-range-max]');
    const track = container.querySelector('[data-range-track]');
    const wrapper = container.parentElement;
    const minInput = wrapper.querySelector('[data-price-min]');
    const maxInput = wrapper.querySelector('[data-price-max]');
    const minLabel = wrapper.querySelector('[data-label-min]');
    const maxLabel = wrapper.querySelector('[data-label-max]');

    if (!minThumb || !maxThumb || !track) return;

    const rangeMax = parseFloat(container.dataset.rangeCeiling) || 100;
    const currencySymbol = container.dataset.currency || '$';
    let minVal = parseFloat(minInput?.value) || 0;
    let maxVal = parseFloat(maxInput?.value) || rangeMax;

    const update = () => {
      const minPct = (minVal / rangeMax) * 100;
      const maxPct = (maxVal / rangeMax) * 100;
      minThumb.style.left = `${minPct}%`;
      maxThumb.style.left = `${maxPct}%`;
      track.style.left = `${minPct}%`;
      track.style.right = `${100 - maxPct}%`;
      minThumb.setAttribute('aria-valuenow', String(Math.round(minVal)));
      maxThumb.setAttribute('aria-valuenow', String(Math.round(maxVal)));
      if (minInput) minInput.value = minVal > 0 ? minVal : '';
      if (maxInput) maxInput.value = maxVal < rangeMax ? maxVal : '';
      if (minLabel) minLabel.textContent = `${currencySymbol}${Math.round(minVal)}`;
      if (maxLabel) maxLabel.textContent = `${currencySymbol}${Math.round(maxVal)}+`;
    };

    const applyDebounced = () => {
      clearTimeout(this._rangeDebounce);
      this._rangeDebounce = setTimeout(() => this.#apply(), 500);
    };

    const drag = (thumb, setter) => {
      const onMove = (e) => {
        const rect = container.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        let pct = ((clientX - rect.left) / rect.width) * 100;
        pct = Math.max(0, Math.min(100, pct));
        const val = Math.round((pct / 100) * rangeMax);
        setter(val);
        update();
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onUp);
        if (this.mdQuery.matches) applyDebounced();
      };
      thumb.addEventListener('mousedown', (e) => {
        e.preventDefault();
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      });
      thumb.addEventListener('touchstart', () => {
        document.addEventListener('touchmove', onMove, { passive: true });
        document.addEventListener('touchend', onUp);
      }, { passive: true });
    };

    const keyboard = (thumb, setter) => {
      thumb.addEventListener('keydown', (e) => {
        const step = e.shiftKey ? Math.max(1, Math.round(rangeMax / 20)) : Math.max(1, Math.round(rangeMax / 100));
        let handled = true;
        switch (e.key) {
          case 'ArrowLeft':
          case 'ArrowDown':
            setter(thumb === minThumb ? minVal - step : maxVal - step); break;
          case 'ArrowRight':
          case 'ArrowUp':
            setter(thumb === minThumb ? minVal + step : maxVal + step); break;
          case 'Home':
            setter(thumb === minThumb ? 0 : minVal + 1); break;
          case 'End':
            setter(thumb === minThumb ? maxVal - 1 : rangeMax); break;
          default:
            handled = false;
        }
        if (handled) {
          e.preventDefault();
          update();
          if (this.mdQuery.matches) applyDebounced();
        }
      });
    };

    drag(minThumb, (v) => { minVal = Math.max(0, Math.min(v, maxVal - 1)); });
    drag(maxThumb, (v) => { maxVal = Math.max(minVal + 1, Math.min(v, rangeMax)); });
    keyboard(minThumb, (v) => { minVal = Math.max(0, Math.min(v, maxVal - 1)); });
    keyboard(maxThumb, (v) => { maxVal = Math.max(minVal + 1, Math.min(v, rangeMax)); });

    update();
  }

  #abort;
}

// Restore filter state on browser back/forward.
window.addEventListener('popstate', () => {
  const cf = document.querySelector('collection-filters');
  if (!cf) return;
  // Re-fetch the section for the URL we just navigated back to.
  const section = cf.closest('.shopify-section');
  const sectionId = section?.id?.replace(/^shopify-section-/, '');
  if (!sectionId) return location.reload();
  const sep = location.search ? '&' : '?';
  fetch(`${location.pathname}${location.search}${sep}sections=${encodeURIComponent(sectionId)}`)
    .then((r) => r.json())
    .then((data) => {
      const html = data[sectionId];
      if (!html) throw new Error('no section html');
      const tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      const newSection = tpl.content.firstElementChild;
      while (section.firstChild) section.removeChild(section.firstChild);
      while (newSection.firstChild) section.appendChild(newSection.firstChild);
    })
    .catch(() => location.reload());
});

customElements.define('collection-filters', CollectionFilters);

export { CollectionFilters };
